import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchProjectFinancialSummaryPaged,
  fetchDashboardRevenueRecognition,
  getDataMode,
} from '@/lib/data-service';
import type {
  ProjectFinancialSummaryRow,
  ProjectFinancialSummaryPagedParams,
} from '@/lib/data-service';

/** The recognition figures a row's chips and fallbacks need, whatever the data mode. */
interface RevenueLookupRow {
  projectId: number;
  revenueToRecognise?: number;
  method?: string;
  budgetRevenue?: number;
}

export interface FinancialSummaryFilters {
  health: string;
  projectType: string;
  signal: string;
}

export interface FinancialSummaryRowEnriched {
  id: number;
  name: string;
  clientNames: string;
  pmName: string;
  projectType: string;
  recognitionMethod: string | null;
  budgetRevenue: number;
  actualSales: number;
  grossMarginPct: number | null;
  wipAmount: number;
  completionPercent: number;
  health: string;
  signals: { label: string; colour: string }[];
}

const PAGE_SIZE = 15;

/**
 * One page of the project financial summary table.
 *
 * `revenueRecognitions` supplies the recognition figures in demo/tour mode. In live mode the
 * hook fetches them for the fifteen projects on the page, so the table costs a bounded lookup
 * rather than the whole active portfolio.
 */
export function usePagedFinancialSummary(
  revenueRecognitions: RevenueLookupRow[],
  isLiveMode: boolean,
  companyId: string | number | undefined,
) {
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<FinancialSummaryFilters>({
    health: 'all',
    projectType: 'all',
    signal: 'all',
  });
  const [rows, setRows] = useState<ProjectFinancialSummaryRow[]>([]);
  const [pageRevenueRows, setPageRevenueRows] = useState<RevenueLookupRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const fetchPage = useCallback(async (p: number, f: FinancialSummaryFilters) => {
    const mode = getDataMode();
    if (mode !== 'live') return;

    setLoading(true);
    const params: ProjectFinancialSummaryPagedParams = {
      page: p,
      pageSize: PAGE_SIZE,
      sortColumn: 'WipAmount',
      sortDirection: 'DESC',
      health: f.health !== 'all' ? f.health : null,
      projectType: f.projectType !== 'all' ? f.projectType : null,
    };

    const response = await fetchProjectFinancialSummaryPaged(params);
    if (!isMountedRef.current) return;
    setRows(response.rows);
    setTotalCount(response.totalCount);

    const revenue = await fetchDashboardRevenueRecognition(
      undefined,
      response.rows.map(r => r.id),
    );
    if (!isMountedRef.current) return;
    setPageRevenueRows(revenue.map(r => ({
      projectId: r.projectId,
      method: r.method ?? undefined,
      budgetRevenue: r.budgetRevenue,
      revenueToRecognise: Math.max(0, r.budgetRevenue - r.recognisedRevenue),
    })));
    setLoading(false);
  }, []);

  // Fetch when page, filters, or company changes
  useEffect(() => {
    if (!isLiveMode || !companyId) return;
    void fetchPage(page, filters);
  }, [page, filters, companyId, isLiveMode, fetchPage]);

  // Signals come off the row itself: usp_Dashboard_ProjectFinancialSummary returns
  // the WIP report's WIP amount, ageing and over-billing per project, so the
  // "WIP High" and "Over-Billed" chips need no WIP row set here.
  const revenueLookup = isLiveMode ? pageRevenueRows : revenueRecognitions;

  const enrichedRows: FinancialSummaryRowEnriched[] = rows.map(p => {
    const rev = revenueLookup.find(r => r.projectId === p.id);
    const actualSales = p.actualSales ?? 0;
    const wipAmount = p.reportWipAmount ?? p.wipAmount ?? 0;
    const agingDays = p.agingDays ?? 0;
    const grossMarginPct = p.grossMarginPercent ?? null;
    const overBilling = p.overBilling ?? 0;
    const revToRecPrj = rev?.revenueToRecognise ?? 0;

    const signals: { label: string; colour: string }[] = [];
    if ((p.costVariancePercent ?? 0) < -10) signals.push({ label: 'Rev Under', colour: 'destructive' });
    if (grossMarginPct !== null && grossMarginPct < 20) signals.push({ label: 'Margin Risk', colour: 'warning' });
    if (wipAmount > 10000 && agingDays > 30) signals.push({ label: 'WIP High', colour: 'warning' });
    if (overBilling > 0) signals.push({ label: 'Over-Billed', colour: 'destructive' });
    if (revToRecPrj > 0) signals.push({ label: 'Unrecognised', colour: 'blue' });

    return {
      id: p.id,
      name: p.name,
      clientNames: p.clientNames ?? '',
      pmName: p.pmName ?? '—',
      projectType: p.projectType ?? '—',
      recognitionMethod: p.revenueRecognitionMethod ?? rev?.method ?? null,
      budgetRevenue: p.budgetRevenue ?? rev?.budgetRevenue ?? 0,
      actualSales,
      grossMarginPct,
      wipAmount,
      completionPercent: p.completionPercent ?? 0,
      health: p.health ?? 'unknown',
      signals: signals.slice(0, 2),
    };
  });

  // Client-side signal filter (signals are computed from joined data, can't be in SQL)
  const filteredRows = filters.signal !== 'all'
    ? enrichedRows.filter(r => r.signals.some(s => s.label.toLowerCase().includes(filters.signal.toLowerCase())))
    : enrichedRows;

  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  const updateFilters = useCallback((newFilters: Partial<FinancialSummaryFilters>) => {
    setFilters(f => ({ ...f, ...newFilters }));
    setPage(0);
  }, []);

  const refresh = useCallback(() => {
    void fetchPage(page, filters);
  }, [page, filters, fetchPage]);

  return {
    rows: filteredRows,
    totalCount,
    page,
    setPage,
    pageSize: PAGE_SIZE,
    pageCount,
    filters,
    updateFilters,
    loading,
    refresh,
  };
}
